<!-- Preview of a GroupContentOverlayImage applied to a ContentUploadFile.

Positioning of the overlay image on top of the file preview is hardcoded
to bottom-right for now. The preview is not exact, both scaling and
positioning are approximates, the results from the videoworker may look
a little different.

-->
<template>
  <div
    v-if="!!file && !!contentOverlayImage"
    class="content-overlay-image-container"
    style="position: absolute; overflow: hidden"
    :style="{
      width: file.containDimensions.containWidth + 'px',
      height: file.containDimensions.containHeight + 'px',
      left: file.containDimensions.xOffset + 'px',
      top: file.containDimensions.yOffset + 'px',
    }"
  >
    <img
      style="position: absolute; transform-origin: bottom right"
      :style="{
        right: tweakedPosition.x + 'px',
        bottom: tweakedPosition.y + 'px',
        width:
          contentOverlayImage.image_width * containPreviewScaleFactor + 'px',
        height:
          contentOverlayImage.image_height * containPreviewScaleFactor + 'px',
        transform: tweakedTransform,
      }"
      :src="contentOverlayImage.image_url"
    >
  </div>
</template>

<script>
import {mapRange} from '@/utils';

export default {
  props: {
    /**
     * A File with the following required attributes:
     *
     * - contentWidth (Number): Width of the actual video/file content.
     * - contentHeight (Number): Height of the actual video/file content.
     *
     * - containDimensions (Object)
     *   - containWidth (Number): Width of the contained preview.
     *   - containHeight (Number): Height of the contained preview.
     *
     */
    file: {
      type: [File, Blob],
      required: true,
    },
    /**
     * Object with the following fields:
     *
     * - image_url (String)
     * - image_width (Number)
     * - image_height (Number)
     */
    contentOverlayImage: {
      type: Object,
      required: true,
    },
  },
  computed: {
    containAspectRatio() {
      if (!this.file.containDimensions) {
        return null;
      }
      return (
        this.file.containDimensions.containWidth /
        this.file.containDimensions.containHeight
      );
    },
    isLandscape() {
      return this.file.contentWidth > this.file.contentHeight;
    },
    /**
     * The overlay image is in 'full-HD space', while 'preview-space' is smaller.
     *
     * This ratio helps map values from one to the other.
     */
    containPreviewScaleFactor() {
      if (!this.file.containDimensions) {
        return 1.0;
      }

      const fullHdWidth = this.isLandscape ? 1920 : 1080;
      const mappedContentWidth = mapRange(
        0,
        this.file.contentWidth,
        0,
        fullHdWidth,
        this.file.contentWidth
      );
      return this.file.containDimensions.containWidth / mappedContentWidth;
    },
    tvformatSpecificTweak() {
      const tweaks = this.contentOverlayImage.tvformat_tweaks || [];
      for (const tweak of tweaks) {
        if (
          !!this.containAspectRatio &&
          !!tweak.tvformat_aspect_ratio &&
          this.aspectRatiosAreVerySimilar(
            tweak.tvformat_aspect_ratio,
            this.containAspectRatio
          )
        ) {
          // Aspect ratios match, this tweak seem to be the right one!
          return tweak;
        }
      }
      return null;
    },
    tweakedPosition() {
      const positionX =
        -1 -
        (this.tvformatSpecificTweak.position_x_offset || 0) *
          this.containPreviewScaleFactor;
      const positionY =
        -1 -
        (this.tvformatSpecificTweak.position_y_offset || 0) *
          this.containPreviewScaleFactor;
      return {
        x: positionX,
        y: positionY,
      };
    },
    // Note: We are currently ignoring any positional offsets defined
    //   in the tweaks here, as it's quite complicated to emulate
    //   properly and doesn't make that much of a difference in this
    //   small preview.
    tweakedTransform() {
      const scale = this.tvformatSpecificTweak.scale || 1.0;
      return this.tvformatSpecificTweak ? `scale(${scale})` : '';
    },
  },
  methods: {
    aspectRatiosAreVerySimilar(ratio1, ratio2) {
      return Math.round(ratio1) == Math.round(ratio2);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/GroupContentOverlayImagePreview.vue